import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';
import { functionsApi } from '../api';
import { functionCategoriesApi, FunctionCategory } from '../api/functionCategoriesApi';
import { FunctionIconSelect } from '../components/common/FunctionIconSelect';
import { resolveIconSelectValue } from '../utils/functionIconOptions';

export const EditFunction: React.FC = () => {
  const { t } = useLang();
  const { isSuperAdmin } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<FunctionCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    route: '',
    icon: '',
    categoryId: '',
    menuOrder: 1,
    description: '',
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) {
      loadFunction();
      loadCategories();
    }
  }, [id]);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const categoriesData = await functionCategoriesApi.getAllCategories();
      setCategories(categoriesData.categories);
    } catch (err: any) {
      console.error('Error loading categories:', err);
      setError('Error loading categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadFunction = async () => {
    try {
      setLoading(true);
      setError(null);
      // Since we don't have a getById endpoint, we'll get all functions and find the one we need
      const functions = await functionsApi.getAllFunctions();
      const func = functions.find(f => f.id === id);
      
      if (!func) {
        setError('Function not found');
        return;
      }

      setFormData({
        name: func.name,
        code: func.code,
        route: func.route,
        icon: resolveIconSelectValue(func.icon || ''),
        categoryId: func.categoryId || '',
        menuOrder: func.menuOrder,
        description: func.description || '',
        isActive: func.isActive
      });
    } catch (err: any) {
      console.error('Error loading function:', err);
      setError(err.response?.data?.message || 'Error occurred while loading function information');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Function name is required';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Function code is required';
    } else if (!/^[A-Z_]+$/.test(formData.code)) {
      newErrors.code = 'Function code can only contain uppercase letters and underscores';
    }

    if (!formData.route.trim()) {
      newErrors.route = 'Route is required';
    } else if (!formData.route.startsWith('/')) {
      newErrors.route = 'Route must start with /';
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'Function category is required';
    }

    if (formData.menuOrder < 1) {
      newErrors.menuOrder = 'Menu order must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const updatePayload: Record<string, unknown> = {
        name: formData.name.trim(),
        icon: formData.icon.trim(),
        categoryId: formData.categoryId,
        menuOrder: formData.menuOrder,
        description: formData.description.trim(),
        isActive: formData.isActive,
      };

      if (isSuperAdmin) {
        updatePayload.code = formData.code.trim().toUpperCase();
        updatePayload.route = formData.route.trim();
      }

      await functionsApi.updateFunction(id!, updatePayload);

      navigate('/functions');
    } catch (err: any) {
      console.error('Error updating function:', err);
      setError(err.response?.data?.message || 'Error occurred while updating function');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checkedValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : checkedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-500"></div>
        </div>
      </div>
    );
  }

  if (error && !formData.name) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <Link 
            to="/functions"
            className="mt-2 inline-block px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            {t('back')} to list
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Breadcrumb */}
      <nav className="flex mb-6 text-sm">
        <span className="text-gray-500">🏠</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-500">{t('mainManagement')}</span>
        <span className="mx-2 text-gray-400">/</span>
        <Link to="/functions" className="text-gray-500 hover:text-cyan-600">{t('functions')}</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-cyan-600 font-medium">{t('editFunction')}</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('editFunction')}</h1>
          <p className="text-gray-600 mt-1">{t('updateFunctionInfo')}</p>
        </div>
        <Link
          to="/functions"
          className="px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition-all duration-200 flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>{t('back')}</span>
        </Link>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Function Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                {t('functionName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter function name"
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            {/* Function Code */}
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                {t('functionCode')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="code"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                disabled={!isSuperAdmin}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors font-mono ${
                  errors.code ? 'border-red-500' : 'border-gray-300'
                } ${!isSuperAdmin ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                placeholder="e.g: SERVICE_PAYMENT"
              />
              {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
              <p className="mt-1 text-xs text-gray-500">
                {isSuperAdmin ? 'Only use uppercase letters and underscores' : 'Only superadmin can change function code'}
              </p>
            </div>

            {/* Route */}
            <div>
              <label htmlFor="route" className="block text-sm font-medium text-gray-700 mb-2">
                {t('functionRoute')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="route"
                name="route"
                value={formData.route}
                onChange={handleInputChange}
                disabled={!isSuperAdmin}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors font-mono ${
                  errors.route ? 'border-red-500' : 'border-gray-300'
                } ${!isSuperAdmin ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                placeholder="/service-payment"
              />
              {errors.route && <p className="mt-1 text-sm text-red-600">{errors.route}</p>}
              {!isSuperAdmin && (
                <p className="mt-1 text-xs text-gray-500">Only superadmin can change function route</p>
              )}
            </div>

            {/* Icon */}
            <div>
              <label htmlFor="icon" className="block text-sm font-medium text-gray-700 mb-2">
                {t('functionIcon')}
              </label>
              <FunctionIconSelect
                id="icon"
                name="icon"
                value={formData.icon}
                onChange={handleInputChange}
              />
              <p className="mt-1 text-xs text-gray-500">Icon name or leave empty to use first letter</p>
            </div>

            {/* Category */}
            <div>
              <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-2">
                {t('functionCategory')} <span className="text-red-500">*</span>
              </label>
              <select
                id="categoryId"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors ${
                  errors.categoryId ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('select')} category</option>
                {loadingCategories ? (
                  <option value="">Loading categories...</option>
                ) : categories.length === 0 ? (
                  <option value="">No categories found</option>
                ) : (
                  categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))
                )}
              </select>
              {errors.categoryId && <p className="mt-1 text-sm text-red-600">{errors.categoryId}</p>}
            </div>

            {/* Menu Order */}
            <div>
              <label htmlFor="menuOrder" className="block text-sm font-medium text-gray-700 mb-2">
                {t('menuOrder')} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="menuOrder"
                name="menuOrder"
                value={formData.menuOrder}
                onChange={handleInputChange}
                min="1"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors ${
                  errors.menuOrder ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.menuOrder && <p className="mt-1 text-sm text-red-600">{errors.menuOrder}</p>}
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('status')}
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  {t('isActive')}
                </label>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              {t('description')}
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
              placeholder="Detailed description of this function..."
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <Link
              to="/functions"
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('cancel')}
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{t('updateFunction')}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 